<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router/composables';
  import { buildUserNav, isNavLinkActive } from '@/domain/layout/app-nav';
  import { IUser } from '@/interfaces/IUser';
  import V2ShellDrawer from './V2ShellDrawer.vue';
  import V2ShellNavItem from './V2ShellNavItem.vue';

  /** The drawer outside a store (plan §4.2): the account and staff pages. */
  const props = defineProps<{
    user: IUser;
  }>();

  const route = useRoute();

  const links = computed(() => buildUserNav({ isStaff: !!props.user.admin }));
</script>

<template>
  <V2ShellDrawer>
    <div class="drawer-user">
      <img class="user-avatar" :src="'assets/img/avatar.jpg'" alt="" />
      <div class="user-text">
        <span class="user-name">{{ user.name }}</span>
        <span class="user-email">{{ user.email }}</span>
      </div>
    </div>

    <nav class="drawer-nav" :aria-label="$t('shell.nav')">
      <ul class="nav-list">
        <li v-for="link in links" :key="link.key">
          <V2ShellNavItem
            :title-key="link.titleKey"
            :to="link.to"
            :icon="link.icon"
            :active="isNavLinkActive(route.path, link.to)"
          />
        </li>
      </ul>
    </nav>
  </V2ShellDrawer>
</template>

<style scoped lang="scss">
  .drawer-user {
    @apply tw-flex tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-4 tw-py-3;

    .user-avatar {
      @apply tw-mr-3 tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-full tw-object-cover;
    }

    .user-text {
      @apply tw-flex tw-min-w-0 tw-flex-col;
    }

    .user-name {
      @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
    }

    .user-email {
      @apply tw-truncate tw-text-xs tw-text-neutral-500;
    }
  }

  .drawer-nav {
    @apply tw-py-2;

    .nav-list {
      @apply tw-m-0 tw-list-none tw-p-0;
    }
  }
</style>
